/**
 * @file useDeliveryOutcomeForm.ts
 * @description Состояние формы условий доставки (phase19.24 шаг 5.2).
 *
 * Держит то, что менеджер правит, и то, чем это посчитано: исход, порядок
 * оплаты и снапшот тарифа. Отдельно от раскладки, потому что решений здесь
 * три и каждое легко сломать молча:
 *  - смена исхода подтягивает допустимые условия оплаты (F31);
 *  - смена перевозчика сбрасывает снапшот: чужой тариф в заказе хуже, чем его
 *    отсутствие;
 *  - выбранный тариф становится ценой для клиента и правит адрес до того ПВЗ,
 *    куда посчитали, — иначе письмо и отгрузка уедут в разные пункты.
 *
 * Четвёртое решение — переинициализация: после сохранения сервер возвращает
 * ДОМЕРЖЕННЫЕ условия (адрес витрины с кодом ПВЗ, город из расчёта), и форма
 * обязана показать именно их. Но только если менеджер ничего не правит прямо
 * сейчас (`dirty`): иначе фоновое обновление вкладки затирало бы набранный
 * адрес (ревью 5.2).
 */

import { useEffect, useRef, useState } from "react";

import {
  deliveryAgreementErrorText,
  setDeliveryAgreementOutcome,
  type DeliveryQuoteSnapshot,
  type OrderDeliveryReceiptDto,
} from "../../../lib/api-delivery-agreement";
import type { OrderPaymentTerms } from "../../../lib/api-orders";
import type { DeliveryQuoteOption } from "../../../lib/api-shop-carriers";
import { buildQuoteSnapshot, type DeliveryQuoteFormValue } from "../../../lib/delivery-quote";
import {
  buildReceiptPayload,
  defaultPaymentTerms,
  isPaymentTermsAllowed,
  receiptFormFromDto,
  validateReceiptForm,
  type ReceiptFormValue,
  type ReceiptIssue,
} from "../../../lib/receipt-method";
import { useNotify } from "../../ui/NotifyProvider";

interface Options {
  orderId: string;
  receipt: OrderDeliveryReceiptDto | null;
  paymentTerms: OrderPaymentTerms | undefined;
  /** Адрес пункта самовывоза заполнен — письмо отправить можно. */
  pickupReady: boolean;
  onSaved: () => void | Promise<void>;
}

export interface DeliveryOutcomeForm {
  value: ReceiptFormValue;
  terms: OrderPaymentTerms;
  setTerms: (next: OrderPaymentTerms) => void;
  quote: DeliveryQuoteSnapshot | null;
  issue: ReceiptIssue | null;
  saving: boolean;
  /** Что мешает ОТПРАВИТЬ условия клиенту; `null` — можно. */
  blockedReason: string | null;
  changeReceipt: (next: ReceiptFormValue) => void;
  applyQuote: (option: DeliveryQuoteOption, form: DeliveryQuoteFormValue) => void;
  /** Сохранить исход; `true` — сервер принял. */
  save: (sending: boolean) => Promise<boolean>;
}

export function useDeliveryOutcomeForm({
  orderId,
  receipt,
  paymentTerms,
  pickupReady,
  onSaved,
}: Options): DeliveryOutcomeForm {
  const notify = useNotify();
  const [value, setValue] = useState<ReceiptFormValue>(() => receiptFormFromDto(receipt));
  const [terms, setTerms] = useState<OrderPaymentTerms>(
    () => paymentTerms ?? defaultPaymentTerms(receiptFormFromDto(receipt).method),
  );
  const [quote, setQuote] = useState<DeliveryQuoteSnapshot | null>(receipt?.quote ?? null);
  const [issue, setIssue] = useState<ReceiptIssue | null>(null);
  const [saving, setSaving] = useState(false);
  /** Менеджер правит поля прямо сейчас — ответ сервера их не затирает. */
  const [dirty, setDirty] = useState(false);
  /** Снапшот, из которого собрана форма: сравниваем ссылкой, а не полями. */
  const applied = useRef(receipt);

  // Свежий снапшот с сервера (после сохранения или перечитывания вкладки)
  // пересобирает форму: сервер домерживает адрес и город, и без этого
  // менеджер видел бы свой черновик вместо того, что реально сохранено.
  useEffect(() => {
    if (dirty || receipt === applied.current) return;
    applied.current = receipt;
    const next = receiptFormFromDto(receipt);
    setValue(next);
    setQuote(receipt?.quote ?? null);
    setIssue(null);
    setTerms(paymentTerms ?? defaultPaymentTerms(next.method));
  }, [dirty, paymentTerms, receipt]);

  function changeReceipt(next: ReceiptFormValue): void {
    setValue(next);
    setDirty(true);
    setIssue(null);
    if (quote && next.carrier !== quote.carrier) setQuote(null);
    setTerms((prev) =>
      isPaymentTermsAllowed(next.method, prev) ? prev : defaultPaymentTerms(next.method),
    );
  }

  /** Порядок оплаты — тоже предмет согласования: его правку не затираем. */
  function changeTerms(next: OrderPaymentTerms): void {
    setDirty(true);
    setTerms(next);
  }

  function applyQuote(option: DeliveryQuoteOption, form: DeliveryQuoteFormValue): void {
    if (!value.carrier) return;
    const snapshot = buildQuoteSnapshot(option, form, value.carrier);
    setQuote(snapshot);
    setDirty(true);
    // Без города сервер расчёт не примет. Сумму сохраняем, но говорим прямо:
    // иначе менеджер думает, что тариф поехал в заказ (и в отгрузку).
    if (!snapshot) {
      notify.warning(
        "Стоимость подставлена, тариф не приложен: укажите город получателя и пересчитайте.",
      );
    }
    // Адрес в карточке обязан совпасть с тем, куда посчитали, — иначе клиент
    // подтвердит одну сумму, а поедет она по другому адресу. Переносим оба
    // режима: ПВЗ — кодом пункта, курьер — адресом из формы расчёта.
    const city = form.city.trim();
    const pvz = form.mode === "PVZ" ? form.pickupPointCode.trim() : "";
    const street = form.mode === "COURIER" ? form.address.trim() : "";
    const quotedAddress =
      pvz !== ""
        ? [city, `ПВЗ ${pvz}`].filter(Boolean).join(", ")
        : street !== ""
          ? [city, street].filter(Boolean).join(", ")
          : "";
    setValue((prev) => ({
      ...prev,
      deliveryAmount: String(option.price),
      freeDelivery: false,
      // Уже введённый вручную адрес не затираем, если он и так про это место:
      // менеджер мог дописать подъезд и домофон, а расчёт их не знает.
      ...(quotedAddress && !prev.address.includes(pvz || street)
        ? { address: quotedAddress }
        : {}),
      ...(city ? { city } : {}),
    }));
  }

  async function save(sending: boolean): Promise<boolean> {
    const problem = validateReceiptForm(value, { pickupReady, sending });
    if (problem) {
      setIssue(problem);
      return false;
    }
    setSaving(true);
    try {
      const result = await setDeliveryAgreementOutcome(orderId, {
        // Адрес витрины (с кодом ПВЗ и координатами) не переписываем, пока
        // менеджер его не трогал: форма знает только строку.
        ...buildReceiptPayload(value, {
          address: receipt?.address ?? null,
          city: receipt?.city ?? null,
        }),
        paymentTerms: terms,
        ...(quote ? { quote } : {}),
      });
      notify.success("Условия доставки сохранены");
      // Сохранённое = серверное: снимаем «грязь», чтобы форма пересобралась из
      // ответа (домерженный адрес с кодом ПВЗ, город из расчёта).
      setDirty(false);
      // Правка отозвала отправленное письмо — говорим сразу: иначе менеджер
      // ждёт подтверждения по ссылке, которой больше нет.
      if (result.agreementReset) {
        notify.warning("Предложение отозвано — отправьте на согласование заново");
      }
      await onSaved();
      return true;
    } catch (err) {
      notify.error(deliveryAgreementErrorText(err, "Не удалось сохранить условия"));
      return false;
    } finally {
      setSaving(false);
    }
  }

  // Блокируем отправку только по незаполненному пункту самовывоза: он
  // чинится в настройках складов, а не в этой форме. Незаполненные поля
  // исхода кнопку не гасят — они покажут подпись под собой по клику.
  const sendIssue = validateReceiptForm(value, { pickupReady, sending: true });

  return {
    value,
    terms,
    setTerms: changeTerms,
    quote,
    issue,
    saving,
    blockedReason: sendIssue?.field === "pickup" ? sendIssue.message : null,
    changeReceipt,
    applyQuote,
    save,
  };
}
